/**
 * Application : zone de dépôt → extraction (moteur) → fiche facture.
 *
 * Aucune action d'écriture : pas d'enregistrement, pas d'envoi, pas de
 * réécriture du fichier. Le seul chemin de données est
 * fichier → mémoire → DOM.
 */
import type { ExtractionResult } from '../lib/model';
import { h } from './dom';
import { dropHereIcon, lockIcon } from './icons';
import { renderInvoice } from './render';
import { renderError, toUiError } from './errors';

export type ExtractFn = (input: Blob | ArrayBuffer | Uint8Array, filename?: string) => Promise<ExtractionResult>;

export interface AppDeps {
  extract: ExtractFn;
  /** Limite de taille locale (20 Mo par défaut). */
  maxBytes?: number;
}

export type AppState = 'idle' | 'loading' | 'result' | 'error';

export interface AppHandle {
  destroy(): void;
  state(): AppState;
}

export const DEFAULT_MAX_BYTES = 20 * 1024 * 1024;
export const READONLY_BANNER =
  'Lecture seule — la facture est analysée dans votre navigateur, elle n’est envoyée à personne.';

const ACCEPTED = /\.(pdf|xml)$/i;

export function createApp(root: HTMLElement, deps: AppDeps): AppHandle {
  const maxBytes = deps.maxBytes ?? DEFAULT_MAX_BYTES;
  let state: AppState = 'idle';
  let busy = false;

  const input = h('input', {
    type: 'file',
    id: 'file-input',
    class: 'visually-hidden',
    accept: '.pdf,.xml,application/pdf,text/xml,application/xml',
    tabindex: '-1',
  });

  const status = h('p', { class: 'status', id: 'app-status', role: 'status', 'aria-live': 'polite' });

  const zone = h(
    'div',
    {
      class: 'dropzone',
      id: 'dropzone',
      role: 'button',
      tabindex: '0',
      'aria-describedby': 'drop-hint',
      'aria-label': 'Choisir une facture à lire (PDF ou XML)',
    },
    input,
    h('span', { class: 'dropzone__icon', 'aria-hidden': 'true' }, dropHereIcon(30)),
    h('p', { class: 'dropzone__title' }, 'Glissez votre facture ici'),
    h('p', { class: 'dropzone__hint', id: 'drop-hint' }, 'ou cliquez pour choisir un fichier — PDF ou XML, 20 Mo maximum.'),
  );

  const resultHost = h('div', { class: 'result', id: 'result-host' });

  const banner = h(
    'div',
    { class: 'readonly-banner', role: 'status' },
    h('span', { class: 'readonly-banner__lock' }, lockIcon(18)),
    h('p', { class: 'readonly-banner__text' }, h('strong', null, 'Lecture seule'), ' — la facture est analysée dans votre navigateur, elle n’est envoyée à personne.'),
  );

  // L'en-tête (h1) et le texte d'accroche sont statiques dans index.html ;
  // l'application ne monte que le bandeau, la zone de dépôt et le résultat.
  const shell = h(
    'div',
    { class: 'app' },
    banner,
    zone,
    status,
    resultHost,
  );

  root.replaceChildren(shell);

  // --- Utilitaires d'état -------------------------------------------------
  function setState(next: AppState): void {
    state = next;
    shell.dataset.state = next;
  }

  function setBusy(value: boolean): void {
    busy = value;
    zone.setAttribute('aria-busy', String(value));
    zone.classList.toggle('is-busy', value);
    input.disabled = value;
  }

  function say(message: string): void {
    status.textContent = message;
  }

  function focusTitle(): void {
    const title = resultHost.querySelector<HTMLElement>('h2');
    title?.focus({ preventScroll: false });
  }

  function reset(): void {
    resultHost.replaceChildren();
    say('');
    setState('idle');
    input.value = '';
    zone.focus();
  }

  // --- Lecture d'un fichier ----------------------------------------------
  function validate(file: File): string | null {
    if (file.size === 0) return 'EMPTY_FILE';
    if (!ACCEPTED.test(file.name)) return 'UNSUPPORTED_FILE';
    if (file.size > maxBytes) return 'FILE_TOO_LARGE';
    return null;
  }

  async function handleFile(file: File): Promise<void> {
    if (busy) return;

    const invalid = validate(file);
    if (invalid) {
      setState('error');
      const view = renderError({ code: invalid, message: file.name }, { filename: file.name, onRetry: openPicker });
      resultHost.replaceChildren(view);
      say('Fichier refusé.');
      focusTitle();
      return;
    }

    setBusy(true);
    setState('loading');
    resultHost.replaceChildren();
    say(`Lecture de ${file.name}…`);

    try {
      const result = await deps.extract(file, file.name);
      resultHost.replaceChildren(renderInvoice(result));
      setState('result');
      say('Facture lue.');
      focusTitle();
    } catch (err) {
      const uiError = toUiError(err);
      resultHost.replaceChildren(renderError(uiError, { filename: file.name, onRetry: openPicker }));
      setState('error');
      say('Lecture impossible.');
      focusTitle();
    } finally {
      setBusy(false);
    }
  }

  // --- Interactions -------------------------------------------------------
  function openPicker(): void {
    input.click();
  }

  const onZoneClick = (event: MouseEvent): void => {
    if (event.target === input) return;
    openPicker();
  };

  const onZoneKey = (event: KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openPicker();
    }
  };

  const onDragOver = (event: DragEvent): void => {
    event.preventDefault();
    zone.classList.add('is-dragover');
  };

  const onDragLeave = (): void => {
    zone.classList.remove('is-dragover');
  };

  const onDrop = (event: DragEvent): void => {
    event.preventDefault();
    zone.classList.remove('is-dragover');
    const file = event.dataTransfer?.files?.[0];
    if (file) void handleFile(file);
  };

  const onInputChange = (): void => {
    const file = input.files?.[0];
    if (file) void handleFile(file);
  };

  // Le bouton « Choisir une autre facture » est délégué : le rendu est recréé à chaque erreur.
  const onResultClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.js-retry')) {
      reset();
      openPicker();
    }
  };

  zone.addEventListener('click', onZoneClick);
  zone.addEventListener('keydown', onZoneKey);
  zone.addEventListener('dragover', onDragOver);
  zone.addEventListener('dragleave', onDragLeave);
  zone.addEventListener('drop', onDrop);
  input.addEventListener('change', onInputChange);
  resultHost.addEventListener('click', onResultClick);

  setState('idle');

  return {
    state: () => state,
    destroy(): void {
      zone.removeEventListener('click', onZoneClick);
      zone.removeEventListener('keydown', onZoneKey);
      zone.removeEventListener('dragover', onDragOver);
      zone.removeEventListener('dragleave', onDragLeave);
      zone.removeEventListener('drop', onDrop);
      input.removeEventListener('change', onInputChange);
      resultHost.removeEventListener('click', onResultClick);
      root.replaceChildren();
    },
  };
}
